import { retryRunnable } from "@mittwald/awesome-node-utils/promises/retry";
import { linearRetryBackoff } from "@mittwald/awesome-node-utils/promises/retry_backoff";
import { useEffect, useState } from "react";
import useForceRerender from "../hooks/useForceRerender";
import debug from "./debug";
import { ResourceLoadingError, UnexpectedResourceLoadingError, } from "./ResourceLoadingError";
import ResourceStore from "./ResourceStore";
import { ResourceState, } from "./types";
const defaultRetryOptions = {
    tryCount: 3,
    backoff: (retry) => 0.3 * 2 ** (retry - 1) * 1000,
};
export class Resource {
    withRetryStore = new ResourceStore();
    state = ResourceState.INITIALIZED;
    data = {
        isSet: false,
    };
    loadingPromise;
    loadResource;
    error;
    dataChangeListener = new Set();
    stateChangeListener = new Set();
    _debug;
    constructor(load) {
        this.loadResource = load;
    }
    load() {
        if (this.loadingPromise === undefined) {
            this.setState(ResourceState.LOADING);
            this.loadingPromise = this.loadResource()
                .then((data) => {
                this.setData(data);
                return data;
            })
                .catch((error) => {
                this.setError(error);
                return error;
            });
        }
        return this.loadingPromise;
    }
    expire(opts = {}) {
        const { autoReload = true } = opts;
        this.loadingPromise = undefined;
        this.setState(ResourceState.EXPIRED);
        const reloadRequired = this.isObserved || ResourceLoadingError.fromError(this.error)?.isObserved;
        if (reloadRequired && autoReload) {
            void this.load();
        }
        this.withRetryStore.expireAll();
    }
    setData(data) {
        const outdatedError = this.error;
        const dataHasChanged = !this.data.isSet || this.data.data !== data;
        this.data = { isSet: true, data: data };
        this.error = undefined;
        this.setState(ResourceState.LOADED);
        ResourceLoadingError.fromError(outdatedError)?.notifyResolved();
        if (dataHasChanged) {
            this.notifyDataChangeListeners();
        }
    }
    setError(error) {
        const outdatedError = this.error;
        const dataHasChanged = this.data.isSet;
        const oldData = this.data.isSet ? this.data.data : undefined;
        this.data = { isSet: false };
        this.error =
            error instanceof ResourceLoadingError
                ? error
                : new UnexpectedResourceLoadingError(error);
        this.setState(ResourceState.ERROR);
        ResourceLoadingError.fromError(outdatedError)?.notifyResolved();
        if (dataHasChanged) {
            this.notifyDataChangeListeners(oldData);
        }
    }
    setState(newState) {
        if (this.state === newState) {
            return;
        }
        const oldState = this.state;
        this.state = newState;
        this.notifyStateChangeListeners(oldState);
    }
    get isObserved() {
        return this.dataChangeListener.size > 0;
    }
    useWatchData(opts) {
        const { optional = false, load = true, throwOnError = true, retry, } = opts ?? {};
        if (retry) {
            const retryOptions = retry === true ? {} : retry;
            return this.withRetry(retryOptions).useWatchData({
                optional,
                load,
            });
        }
        const rerender = useForceRerender();
        const undefinedResult = undefined;
        useEffect(() => {
            if (load) {
                return this.onDataChanged(rerender);
            }
        }, [this, load]);
        if (!load) {
            return undefinedResult;
        }
        if (this.error) {
            const isUnexpectedError = this.error instanceof UnexpectedResourceLoadingError;
            if (!throwOnError) {
                return undefinedResult;
            }
            else if (isUnexpectedError) {
                throw this.error;
            }
            else if (optional) {
                return undefinedResult;
            }
            else {
                throw this.error;
            }
        }
        void this.load();
        if (this.data.isSet) {
            return this.data.data;
        }
        if (!this.loadingPromise) {
            throw new Error("Unexpected state");
        }
        throw this.loadingPromise;
    }
    useWatchState() {
        const [state, setState] = useState(this.state);
        const handleStateChange = () => {
            setState(this.state);
        };
        useEffect(() => {
            return this.onStateChanged(handleStateChange);
        }, [this]);
        return state;
    }
    onDataChanged(handler) {
        this.dataChangeListener.add(handler);
        return () => this.dataChangeListener.delete(handler);
    }
    notifyDataChangeListeners(oldData) {
        const newData = this.data.isSet ? this.data.data : undefined;
        this.dataChangeListener.forEach((cb) => cb(newData, oldData));
    }
    onStateChanged(handler) {
        this.stateChangeListener.add(handler);
        return () => this.stateChangeListener.delete(handler);
    }
    withRetry(options = {}) {
        const retries = options.tryCount ?? defaultRetryOptions.tryCount;
        const backoff = options.backoff ?? defaultRetryOptions.backoff;
        const resourceWithRetry = new Resource(() => retryRunnable(() => this.loadResource(), {
            retries,
            getRetryBackoff: typeof backoff === "number"
                ? linearRetryBackoff(backoff / 1000)
                : backoff,
        }));
        return this.withRetryStore.getOrSet(options, resourceWithRetry);
    }
    notifyStateChangeListeners(oldState) {
        this.stateChangeListener.forEach((cb) => cb(this.state, oldState));
    }
    getDebugPrefix() {
        return this.constructor.name;
    }
    get debug() {
        if (!this._debug) {
            this._debug = debug.extend(this.getDebugPrefix());
        }
        return this._debug;
    }
}
//# sourceMappingURL=data:application/json;base64,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